import React, { useMemo } from "react";
import { IResourceComponentsProps, useTranslate, useGetIdentity } from "@refinedev/core";
import { Edit, useForm, useSelect } from "@refinedev/antd";
import { Form, Input, Select, DatePicker, Col, Row } from "antd";
import dayjs from "dayjs";
import { TunerRole, UserRole, SuperadminRole, ServiceRole } from "../../models/user";

export const UserEdit: React.FC<IResourceComponentsProps> = () => {
    const translate = useTranslate();
    const { data: currentUser } = useGetIdentity();
    const currentUserRoleId = (currentUser as any)?.role?.id;
    const isSuperAdmin = currentUserRoleId === SuperadminRole.id || 
                         currentUserRoleId === String(SuperadminRole.id) || 
                         Number(currentUserRoleId) === SuperadminRole.id;

    const { formProps, saveButtonProps, queryResult, form } = useForm(
        {
            onMutationError: (data, variables, context, isAutoSave) => {
                const errors = data?.response?.data?.errors;
                if ('email' in errors) {
                    if (errors['email'] === 'emailAlreadyExists')
                    form.setFields([
                        {
                            name: 'email',
                            errors: [translate('users.errors.emailAlreadyExists')],
                        },
                    ]);
                }
            }
        }
    );

    const handleFinish = async (values: any) => {
        // Values are already in flat format (serviceProviderId, tenantId)
        // Just pass them through to the original onFinish
        if (formProps.onFinish) {
            return formProps.onFinish(values);
        }
    };

    const usersData = queryResult?.data?.data;
    const selectedRoleId = Form.useWatch(['role', 'id'], form);

    const { selectProps: roleSelectProps } = useSelect({
        resource: "roles",
        defaultValue: usersData?.role?.id,
        optionLabel: "name",
    });

    // Filter role options to hide Superadmin and Service roles if user is not superadmin
    // But include the current user's role if it's Superadmin or Service (so it's visible but can't be changed)
    // and transform role names to use translations
    const filteredRoleOptions = useMemo(() => {
        if (!roleSelectProps.options) return [];
        let options = roleSelectProps.options;
        
        // Filter out Superadmin and Service roles if user is not superadmin
        if (!isSuperAdmin) {
            const currentUserRoleId = usersData?.role?.id;
            options = options.filter((option: any) => {
                const roleId = option.value;
                // Show Superadmin and Service only if it's the current user's role
                if (roleId === SuperadminRole.id || roleId === ServiceRole.id) {
                    return roleId === currentUserRoleId;
                }
                return true;
            });
        }
        
        // Transform role names to use translations
        return options.map((option: any) => {
            const roleName = option.label || option.name;
            const translationKey = `users.fields.roleValues.${roleName}`;
            return {
                ...option,
                label: translate(translationKey, roleName),
            };
        });
    }, [roleSelectProps.options, isSuperAdmin, usersData?.role?.id, translate]);

    const { selectProps: accountSelectProps } = useSelect({
        resource: "accounts",
        defaultValue: usersData?.account?.id,
        optionLabel: "name",
        queryOptions: {
            enabled: selectedRoleId === UserRole.id,
        },
    });

    const { selectProps: serviceProviderSelectProps } = useSelect({
        resource: "service-providers",
        defaultValue: usersData?.serviceProvider?.id,
        optionLabel: "name",
        queryOptions: {
            enabled: selectedRoleId === TunerRole.id,
        },
    });

    const { selectProps: tenantSelectProps } = useSelect({
        resource: "tenants",
        defaultValue: usersData?.tenant?.id,
        optionLabel: "name",
        queryOptions: {
            enabled: isSuperAdmin,
        },
    });

    // Transform initial form values from nested objects to flat fields
    React.useEffect(() => {
        if (usersData) {
            const initialValues: any = {};
            if (usersData.serviceProvider?.id) {
                initialValues.serviceProviderId = usersData.serviceProvider.id;
            }
            if (usersData.tenant?.id) {
                initialValues.tenantId = usersData.tenant.id;
            }
            if (Object.keys(initialValues).length > 0) {
                form.setFieldsValue(initialValues);
            }
        }
    }, [usersData, form]);

    const handleFormValuesChange = (changedValues: any, allValues: any) => {
        if ('role' in changedValues && changedValues.role?.id !== undefined) {
            const roleId = changedValues.role.id;
            if (roleId !== TunerRole.id) {
                form.setFieldsValue({
                    serviceProviderId: undefined,
                });
            }
            if (roleId !== UserRole.id) {
                form.setFieldsValue({
                    account: undefined,
                });
            }
        }
    };

    return (
        <Edit saveButtonProps={saveButtonProps}>
            <Form {...formProps} layout="vertical" form={form} onValuesChange={handleFormValuesChange} onFinish={handleFinish}>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        <Form.Item
                            label={translate("users.fields.email")}
                            name={["email"]}
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        <Form.Item
                            label={translate("users.fields.status")}
                            name={["status", "id"]}
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Select options={[
                                { label: translate('users.fields.statusValues.active'), value: 1 },
                                { label: translate('users.fields.statusValues.inactive'), value: 2 },
                            ]} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        <Form.Item
                            label={translate("users.fields.firstName")}
                            name={["firstName"]}
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        <Form.Item
                            label={translate("users.fields.lastName")}
                            name={["lastName"]}
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        <Form.Item
                            label={translate("users.fields.role")}
                            name={["role", "id"]}
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Select {...roleSelectProps} options={filteredRoleOptions} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        {selectedRoleId === UserRole.id && (
                            <Form.Item
                                label={translate("users.fields.account")}
                                name={["account", "id"]}
                                rules={[
                                    {
                                        required: false,
                                    },
                                ]}
                            >
                                <Select {...accountSelectProps} />
                            </Form.Item>
                        )}
                        {selectedRoleId === TunerRole.id && (
                            <Form.Item
                                label={translate("users.fields.serviceProvider")}
                                name="serviceProviderId"
                                rules={[
                                    {
                                        required: false,
                                    },
                                ]}
                            >
                                <Select {...serviceProviderSelectProps} />
                            </Form.Item>
                        )}
                    </Col>
                </Row>
                {isSuperAdmin && (
                    <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                        <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                            <Form.Item
                                label={translate("users.fields.tenant")}
                                name="tenantId"
                                rules={[
                                    {
                                        required: false,
                                    },
                                ]}
                            >
                                <Select {...tenantSelectProps} />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
            </Form>
        </Edit>
    );
};
